import React, { useState } from 'react';
import { Box, SwipeableDrawer } from '@mui/material';

interface NotificationSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const noop = () => undefined;

/**
 * Gives a touch that starts in a list scrolled away from its top to that list
 * for the whole gesture, whichever way the finger then moves. Without this,
 * MUI picks by the first few pixels of movement, so a push up at the end of
 * the list followed by a pull down would drag the sheet instead of scrolling.
 * defaultMuiPrevented is MUI's documented way for a child to keep a touch.
 */
const keepScrolledListsScrolling = (event: React.TouchEvent<HTMLElement>) => {
  for (let node = event.target as HTMLElement | null; node && node !== event.currentTarget; node = node.parentElement) {
    if (node.scrollTop > 0) {
      (event.nativeEvent as TouchEvent & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
      return;
    }
  }
};

/**
 * The bell's panel on phones: a bottom sheet that closes when dragged down
 * from its handle or header, or from a list at its top, or when flicked down.
 * A drag that starts while the list is scrolled down scrolls the list. Drags
 * that the sheet takes are preventDefault-ed by MUI, so they never scroll or
 * rubber-band the page behind it.
 */
export const NotificationSheet: React.FC<NotificationSheetProps> = ({ open, onClose, children }) => {
  // The content mounts when the sheet opens and unmounts once it has slid
  // away, so the panel is fresh each time, as it is in the desktop popover.
  const [exited, setExited] = useState(!open);
  if (open && exited) setExited(false);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={noop}
      // It opens from the bell only. An edge swipe area would fight the
      // browser's own edge gestures on iOS, as MUI's docs warn.
      disableSwipeToOpen
      disableDiscovery
      // Close a quarter of the way down, or on a flick, as native sheets do.
      hysteresis={0.25}
      SlideProps={{ onExited: () => setExited(true) }}
      PaperProps={{
        role: 'dialog',
        'aria-label': 'Notifications',
        onTouchStart: keepScrolledListsScrolling,
        sx: {
          borderRadius: '16px 16px 0 0',
          // vh is the height with Safari's toolbars hidden, so with them shown
          // 88vh can push the header off the top. dvh tracks the visible height.
          maxHeight: '88vh',
          '@supports (height: 100dvh)': { maxHeight: '88dvh' },
          display: 'flex',
          flexDirection: 'column',
          // Only the panel's list scrolls. A scrollable sheet would take the
          // drags meant to close it.
          overflow: 'hidden',
          // Clear the home indicator where the page reports a safe area.
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      <Box aria-hidden sx={{ flex: 'none', pt: 1, pb: 0.5 }}>
        <Box sx={{ width: 36, height: 5, borderRadius: 2.5, bgcolor: 'grey.400', mx: 'auto' }} />
      </Box>
      {!exited && children}
    </SwipeableDrawer>
  );
};

export default NotificationSheet;
